import { PAGE_EDITOR_IDS } from '@/components/admin/experiences/page-editor/contracts';
import { VariablePill } from '@/components/admin/variables/variable-pill';
import {
  CONDITION_NOYAUX,
  CONDITION_OPERATOR_LABEL,
  choiceButtons,
  conditionPaint,
  lostButtons,
  toggleButton,
  toggleNoyau,
} from '@/lib/display-conditions';
import { cn } from '@/lib/utils';
import type { VariableSources } from '@/lib/variable-pill';
import type { DisplayCondition } from '@matter_extractor/shared';
import { noyauOf } from '@matter_extractor/shared';
import { X } from 'lucide-react';
import { useId } from 'react';

/**
 * UNE condition d'affichage, à la forme de sa variable (refonte modulaire §9.2,
 * H67 ; maquette `ConditionsParNature` ; issue #150).
 *
 * « Une condition prend la forme de la variable qu'elle vise » : the pill of the
 * variable, then what its nature lets an operator say and nothing else - six
 * noyaux to tick, the Boutons of a page to tick, or two answers to choose
 * between. The operator is CLOSED by the schema (H67), so there is no operator
 * to pick for a noyau or a choice (« est l'un de » is written, not chosen), and
 * the text of an Input is never compared: « est rempli » or « est vide ».
 *
 * A list never goes down to nothing: the schema refuses an empty one, and the
 * last ticked value is therefore the one box that does not untick - removing
 * the condition is the gesture that says « plus de filtre ».
 *
 * The pill is painted by the zone's own rule (`variableStandingOf`), so a
 * condition that cites a variable of this very page wears the yellow of « hors
 * de portée », and one whose variable is gone the grey of « inconnu », with the
 * sentence the save box will say - read here first, where it is fixed.
 */

const OPTION = 'flex items-center gap-2 rounded-md border-2 bg-white px-2.5 py-1.5 text-sm';

export interface ConditionCardProps {
  readonly condition: DisplayCondition;
  readonly sources: VariableSources;
  /** The page the block stands on, whose reach the pill is judged by (H60). */
  readonly pageIndex: number;
  readonly readOnly: boolean;
  readonly onChange: (next: DisplayCondition) => void;
  readonly onRemove: () => void;
}

export function ConditionCard({
  condition,
  sources,
  pageIndex,
  readOnly,
  onChange,
  onRemove,
}: ConditionCardProps) {
  const name = useId();
  const paint = conditionPaint(condition, sources, pageIndex);
  const oneOf = condition.kind === 'noyau' || condition.kind === 'choice';

  /** Two answers, as the mockup draws them: a radio each, the chosen one in bleu nuit. */
  function answers<Value extends string>(
    options: readonly { readonly value: Value; readonly label: string }[],
    current: Value,
    pick: (value: Value) => void,
  ) {
    return (
      <fieldset className="space-y-1.5">
        <legend className="sr-only">Condition sur {paint.label}</legend>
        {options.map((option) => (
          <label
            key={option.value}
            className={cn(
              OPTION,
              current === option.value ? 'border-bleu-nuit' : 'border-gray-200',
            )}
          >
            <input
              type="radio"
              name={name}
              data-testid={PAGE_EDITOR_IDS.conditionValue}
              data-value={option.value}
              checked={current === option.value}
              disabled={readOnly}
              onChange={() => pick(option.value)}
              className="size-4 accent-bleu-nuit"
            />
            {option.label}
          </label>
        ))}
      </fieldset>
    );
  }

  function body() {
    switch (condition.kind) {
      case 'noyau':
        return (
          <fieldset className="space-y-1.5">
            <legend className="sr-only">Les noyaux qui affichent ce bloc</legend>
            {CONDITION_NOYAUX.map((code) => {
              const ticked = condition.noyaux.includes(code);
              const last = ticked && condition.noyaux.length === 1;
              const noyau = noyauOf(code);
              return (
                <label
                  key={code}
                  className={cn(OPTION, ticked ? 'border-bleu-nuit' : 'border-gray-200')}
                >
                  <input
                    type="checkbox"
                    data-testid={PAGE_EDITOR_IDS.conditionValue}
                    data-value={code}
                    checked={ticked}
                    disabled={readOnly || last}
                    onChange={() => onChange(toggleNoyau(condition, code))}
                    className="size-4 accent-bleu-nuit"
                  />
                  {/* The official colour is DATA of `shared` (noyau.md §2.1), the
                      operator's reference: an inline colour, never a class. */}
                  <span
                    aria-hidden
                    className="size-2.5 shrink-0 rounded-pill"
                    style={{ backgroundColor: noyau.color }}
                  />
                  <span className="min-w-0 truncate">
                    {noyau.name} <span className="text-gray-600">· {code}</span>
                  </span>
                </label>
              );
            })}
            <p className="text-gray-600 text-xs leading-4">
              Un noyau au moins : retirez la condition pour ne plus filtrer.
            </p>
          </fieldset>
        );
      case 'choice': {
        const buttons = choiceButtons(sources.draft.pages, condition.pageId);
        const lost = lostButtons(condition, sources.draft.pages);
        const live = condition.buttonIds.filter((id) => !lost.includes(id));
        return (
          <fieldset className="space-y-1.5">
            <legend className="sr-only">Les boutons qui affichent ce bloc</legend>
            {buttons.map((button) => {
              const ticked = live.includes(button.id);
              return (
                <label
                  key={button.id}
                  className={cn(OPTION, ticked ? 'border-bleu-nuit' : 'border-gray-200')}
                >
                  <input
                    type="checkbox"
                    data-testid={PAGE_EDITOR_IDS.conditionValue}
                    data-value={button.id}
                    checked={ticked}
                    disabled={readOnly || (ticked && live.length === 1)}
                    onChange={() =>
                      onChange(toggleButton(condition, button.id, sources.draft.pages))
                    }
                    className="size-4 accent-bleu-nuit"
                  />
                  <span className="min-w-0 truncate">
                    « {button.label || 'Bouton sans libellé'} »
                  </span>
                </label>
              );
            })}
            {lost.length === 0 ? null : (
              <p role="alert" className="text-rouge-deep text-xs leading-4">
                {lost.length === 1
                  ? 'Un bouton visé n’est plus sur sa page : l’enregistrement refusera cette condition tant qu’il y reste.'
                  : 'Des boutons visés ne sont plus sur leur page : l’enregistrement refusera cette condition tant qu’ils y restent.'}
              </p>
            )}
          </fieldset>
        );
      }
      case 'checkbox':
        return answers(
          [
            { value: 'checked', label: CONDITION_OPERATOR_LABEL.checkbox.checked },
            { value: 'unchecked', label: CONDITION_OPERATOR_LABEL.checkbox.unchecked },
          ],
          condition.operator,
          (operator) => onChange({ ...condition, operator }),
        );
      case 'input':
        return answers(
          [
            { value: 'filled', label: CONDITION_OPERATOR_LABEL.input.filled },
            { value: 'empty', label: CONDITION_OPERATOR_LABEL.input.empty },
          ],
          condition.operator,
          (operator) => onChange({ ...condition, operator }),
        );
      case 'objective':
        return answers(
          [
            { value: 'succeeded', label: CONDITION_OPERATOR_LABEL.objective.succeeded },
            {
              value: 'not-succeeded',
              label: CONDITION_OPERATOR_LABEL.objective['not-succeeded'],
            },
          ],
          condition.operator,
          (operator) => onChange({ ...condition, operator }),
        );
      default:
        return null;
    }
  }

  return (
    <div
      data-testid={PAGE_EDITOR_IDS.condition}
      data-kind={condition.kind}
      className="space-y-2 rounded-md border-2 border-gray-200 bg-gray-50 p-2"
    >
      <div className="flex items-center gap-2">
        <VariablePill
          paint={paint}
          interactive={false}
          className="min-w-0 truncate text-xs leading-5"
        />
        {oneOf ? (
          <span className="shrink-0 font-semibold text-gray-800 text-sm">
            {CONDITION_OPERATOR_LABEL.oneOf}
          </span>
        ) : null}
        {readOnly ? null : (
          <button
            type="button"
            data-testid={PAGE_EDITOR_IDS.conditionRemove}
            aria-label={`Retirer la condition sur ${paint.label}`}
            onClick={onRemove}
            className="ml-auto inline-flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-md border-2 border-gray-200 bg-white text-gray-600 hover:border-gray-400 hover:text-bleu-nuit focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <X aria-hidden className="size-4" />
          </button>
        )}
      </div>

      {paint.ink === 'out-of-scope' ? (
        <p role="alert" className="text-jaune-deep text-xs leading-4">
          Cette variable n’existe pas encore sur cette page : une condition ne cite qu’une variable
          des pages d’avant, et l’enregistrement la refusera ici.
        </p>
      ) : paint.ink === 'unknown' && condition.kind !== 'noyau' ? (
        <p role="alert" className="text-rouge-deep text-xs leading-4">
          Cette variable n’existe plus dans la timeline : l’enregistrement refusera la condition
          tant qu’elle la cite.
        </p>
      ) : null}

      {body()}
    </div>
  );
}
